import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, FileText, Receipt, CheckCircle2, BadgePercent, Ban } from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { Sheet } from '@/components/ui/Sheet'
import { Modal } from '@/components/ui/Modal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import type { DropdownItem } from '@/components/ui/DropdownMenu'
import { RevenuesService, type Revenue } from '@/services/modules/RevenuesService'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { useAuthority } from '@/hooks/useAuthority'

/** Formas de pagamento aceitas na baixa MANUAL (marcar como pago). */
const MANUAL_PAYMENT_METHODS = [
  { value: 'TRANSFERÊNCIA BANCÁRIA', label: 'Transferência' },
  { value: 'DEPÓSITO', label: 'Depósito' },
  { value: 'DINHEIRO', label: 'Dinheiro' },
  { value: 'PIX', label: 'PIX' },
]

/**
 * Ações de fatura compartilhadas entre a lista global (/revenues) e a lista de
 * faturas de um assinante. Garante MENU e regras idênticos nos dois lugares.
 *
 * Uso:
 *   const { rowActions, dialogs } = useRevenueActions(refresh)
 *   <DataTable rowActions={rowActions} ... />
 *   {dialogs}
 */
export function useRevenueActions(refresh: () => void) {
  const navigate = useNavigate()

  const [payTarget, setPayTarget] = useState<Revenue | null>(null)
  const [payDate, setPayDate] = useState('')
  const [payMethod, setPayMethod] = useState('')
  const [payConfirm, setPayConfirm] = useState(false)
  const [boletoTarget, setBoletoTarget] = useState<Revenue | null>(null)
  const [boletoDue, setBoletoDue] = useState('')
  const [waiveTarget, setWaiveTarget] = useState<Revenue | null>(null)
  const [cancelTarget, setCancelTarget] = useState<Revenue | null>(null)
  const [busy, setBusy] = useState(false)

  // O menu só oferece o que o cargo permite — a rota recusaria de qualquer jeito.
  const { can } = useAuthority()

  function rowActions(r: Revenue): DropdownItem[] {
    const items: DropdownItem[] = []
    const isBoleto = (r.method ?? '').toUpperCase() === 'BOLETO'
    const isPending = r.status === 'PENDENTE'

    const customerHref = r.subscriberId
      ? `/commercial/subscribers/${r.subscriberId}/editar`
      : r.companyId ? `/commercial/companies/${r.companyId}/editar` : null
    if (customerHref) {
      items.push({ key: 'customer', label: 'Ver cliente', icon: <User size={14} />, onClick: () => navigate(customerHref) })
    }
    items.push({ key: 'detail', label: 'Detalhes da fatura', icon: <FileText size={14} />, onClick: () => navigate(`/revenues/${r.id}`) })

    if (isBoleto) {
      items.push({
        key: 'boleto', label: 'Ver boleto', icon: <Receipt size={14} />,
        onClick: () => {
          if (r.bankSlipUrl) window.open(r.bankSlipUrl, '_blank', 'noopener,noreferrer')
          else toast.error({ title: 'Boleto indisponível', description: 'Esta fatura ainda não possui boleto gerado.' })
        },
      })
    }

    if (isPending) {
      if (isBoleto && can('financial.revenues.update_bank_slip')) {
        items.push({
          key: 'update-boleto', label: 'Atualizar boleto', icon: <Receipt size={14} />, divider: true,
          onClick: () => { setBoletoTarget(r); setBoletoDue(r.dueDate ?? '') },
        })
      }
      const before = items.length
      if (can('financial.revenues.mark_paid')) {
        items.push({
          key: 'pay', label: 'Marcar como pago', icon: <CheckCircle2 size={14} />,
          onClick: () => { setPayTarget(r); setPayDate(''); setPayMethod('') },
        })
      }
      if (can('financial.revenues.mark_waived')) {
        items.push({
          key: 'waive', label: 'Marcar como isento', icon: <BadgePercent size={14} />,
          onClick: () => setWaiveTarget(r),
        })
      }
      if (can('financial.revenues.mark_cancelled')) {
        items.push({
          key: 'cancel', label: 'Cancelar fatura', icon: <Ban size={14} />, danger: true,
          onClick: () => setCancelTarget(r),
        })
      }
      // Separa o bloco de ações do de consulta (o "Atualizar boleto" já separa).
      if (items.length > before && !items.some(i => i.key === 'update-boleto')) {
        items[before] = { ...items[before], divider: true }
      }
    }

    return items
  }

  async function submitUpdateBoleto() {
    if (!boletoTarget || !boletoDue) return
    setBusy(true)
    try {
      await RevenuesService.updateBankSlip(boletoTarget.id, boletoDue)
      toast.success({ title: 'Boleto atualizado', description: 'Vencimento alterado com sucesso.' })
      setBoletoTarget(null); refresh()
    } catch (e) {
      toast.error({ title: 'Erro ao atualizar', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  function openPayConfirm() {
    if (!payDate || !payMethod) {
      toast.error({ title: 'Campos obrigatórios', description: 'Informe a data e a forma de pagamento.' })
      return
    }
    setPayConfirm(true)
  }

  async function submitPay() {
    if (!payTarget) return
    setBusy(true)
    try {
      await RevenuesService.markAsPaid(payTarget.id, { date: payDate, paymentMethod: payMethod })
      toast.success({ title: 'Fatura paga', description: 'Baixa manual registrada e cobrança desvinculada do Asaas.' })
      setPayConfirm(false); setPayTarget(null); refresh()
    } catch (e) {
      toast.error({ title: 'Erro ao dar baixa', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  async function submitWaive() {
    if (!waiveTarget) return
    setBusy(true)
    try {
      await RevenuesService.markAsWaived(waiveTarget.id)
      toast.success({ title: 'Fatura isenta', description: 'Comissões removidas e cobrança desvinculada do Asaas.' })
      setWaiveTarget(null); refresh()
    } catch (e) {
      toast.error({ title: 'Erro ao isentar', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  async function submitCancel() {
    if (!cancelTarget) return
    setBusy(true)
    try {
      await RevenuesService.markAsCancelled(cancelTarget.id)
      toast.success({ title: 'Fatura cancelada', description: 'Comissões removidas e cobrança desvinculada do Asaas.' })
      setCancelTarget(null); refresh()
    } catch (e) {
      toast.error({ title: 'Erro ao cancelar', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  const dialogs = (
    <>
      {/* Atualizar boleto */}
      <Modal
        open={!!boletoTarget}
        onClose={() => setBoletoTarget(null)}
        title="Atualizar boleto"
        size="sm"
        footer={<>
          <Button variant="ghost" onClick={() => setBoletoTarget(null)} disabled={busy}>Cancelar</Button>
          <Button onClick={submitUpdateBoleto} loading={busy} disabled={!boletoDue}>Salvar</Button>
        </>}
      >
        <Input label="Novo vencimento" type="date" value={boletoDue} onChange={(e) => setBoletoDue(e.target.value)} />
      </Modal>

      {/* Marcar como pago */}
      <Sheet
        open={!!payTarget}
        onClose={() => setPayTarget(null)}
        title="Marcar como pago"
        subtitle="Baixa manual — a cobrança será desvinculada e excluída do Asaas."
        size="md"
        footer={<>
          <Button variant="ghost" onClick={() => setPayTarget(null)} disabled={busy}>Cancelar</Button>
          <Button onClick={openPayConfirm} disabled={busy}>Continuar</Button>
        </>}
      >
        <div className="space-y-4">
          <Input label="Data do pagamento" type="date" required value={payDate} onChange={(e) => setPayDate(e.target.value)} />
          <Select label="Forma de pagamento" required value={payMethod} onChange={(e) => setPayMethod(e.target.value)} options={MANUAL_PAYMENT_METHODS} placeholder="Selecione" clearable={false} />
        </div>
      </Sheet>

      <ConfirmDialog
        open={payConfirm}
        onClose={() => setPayConfirm(false)}
        onConfirm={submitPay}
        loading={busy}
        title="Confirmar baixa manual?"
        description={`Marcar a fatura ${payTarget?.code ?? ''} como PAGA em ${payDate} via ${MANUAL_PAYMENT_METHODS.find((m) => m.value === payMethod)?.label ?? payMethod}. A cobrança será excluída do Asaas. Deseja continuar?`}
        confirmLabel="Confirmar pagamento"
      />

      <ConfirmDialog
        open={!!waiveTarget}
        onClose={() => setWaiveTarget(null)}
        onConfirm={submitWaive}
        loading={busy}
        variant="danger"
        title="Marcar como isenta?"
        description={`A fatura ${waiveTarget?.code ?? ''} ficará ISENTA: as comissões serão removidas e a cobrança será desvinculada e excluída do Asaas. Esta ação não pode ser desfeita.`}
        confirmLabel="Marcar isenta"
      />

      <ConfirmDialog
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={submitCancel}
        loading={busy}
        variant="danger"
        title="Cancelar fatura?"
        description={`A fatura ${cancelTarget?.code ?? ''} será CANCELADA: as comissões serão removidas e a cobrança será desvinculada e excluída do Asaas. Esta ação não pode ser desfeita.`}
        confirmLabel="Cancelar fatura"
      />
    </>
  )

  return { rowActions, dialogs }
}
